import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Table, Accordion, Spinner, Badge, Modal } from 'react-bootstrap';
import { showToast as toast } from '../toastManager';
import axios from 'axios';

const SectionTags = ({ ViewedPage, shop_domain, app_url, isInSettingsContainer }) => {
  useEffect(() => {
    ViewedPage(42);
  }, []);

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAllTag, setShowAllTag] = useState(null);
  const [activeKeys, setActiveKeys] = useState(['add-tag']);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${app_url}/api/settings/sections-tags`, { withCredentials: true });
      if (response.data.success) {
        setTags(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to load section tags');
      }
    } catch (error) {
      console.error('Error loading section tags:', error);
      toast.error('Error loading section tags. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shop_domain && app_url) {
      loadTags();
    }
  }, [shop_domain, app_url]);

  const saveTags = async (updatedTags) => {
    const response = await axios.put(`${app_url}/api/settings/sections-tags`, {
      sections_tags: updatedTags
    }, { withCredentials: true });
    if (response.data.success) {
      setTags(updatedTags);
      return true;
    } else {
      toast.error(response.data.message || 'Failed to save section tags');
      return false;
    }
  };

  const suggestions = categoryName.trim()
    ? tags.filter(tag => tag.category.toLowerCase().startsWith(categoryName.trim().toLowerCase()))
    : [];

  const handleAddTag = async (e) => {
    e.preventDefault();
    const trimmedCategory = categoryName.trim();
    const trimmedSubcategory = subcategoryName.trim();

    if (!trimmedCategory) {
      toast.error('Please enter a category name');
      return;
    }
    if (!trimmedSubcategory) {
      toast.error('Please enter a subcategory name');
      return;
    }

    try {
      setSaving(true);
      const existingTag = tags.find(tag => tag.category.toLowerCase() === trimmedCategory.toLowerCase());

      let updatedTags;
      if (existingTag) {
        if (existingTag.subcategories.some(sc => sc.toLowerCase() === trimmedSubcategory.toLowerCase())) {
          toast.error('This subcategory already exists in the selected category');
          return;
        }
        updatedTags = tags.map(tag =>
          tag.id === existingTag.id
            ? { ...tag, subcategories: [...tag.subcategories, trimmedSubcategory] }
            : tag
        );
      } else {
        const newTag = {
          id: Date.now().toString(),
          category: trimmedCategory,
          subcategories: [trimmedSubcategory],
          created_at: new Date().toISOString()
        };
        updatedTags = [...tags, newTag];
      }

      const success = await saveTags(updatedTags);
      if (success) {
        setCategoryName('');
        setSubcategoryName('');
        setShowSuggestions(false);
        toast.success(existingTag ? 'Subcategory added to existing category' : 'Section tag added successfully');
      }
    } catch (error) {
      console.error('Error adding section tag:', error);
      toast.error('Error adding section tag. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid className="p-0">
      <div className='main-title mt-4'>
        <h2>Section Tags</h2>
      </div>
      <Row>
        <Col>
          <div className="main-accordion-body mt-4 p-2">
            <Accordion activeKey={activeKeys[0] || null} onSelect={(k) => setActiveKeys(k ? [k] : [])} className="section-tags-accordion">
              <Accordion.Item eventKey="add-tag">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="sxi-accordion-header-with-icon">
                      <span>Add Section Tag</span>
                    </div>
                    <span className={`campaign-accordion-chevron ${activeKeys.includes('add-tag') ? 'open' : ''}`} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Form onSubmit={handleAddTag}>
                    <Row className='mt-1'>
                      <Col md={5}>
                        <Form.Group className="mb-3" style={{ position: 'relative' }} ref={suggestionsRef}>
                          <Form.Label>Category</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter category name"
                            value={categoryName}
                            onChange={(e) => { setCategoryName(e.target.value); setShowSuggestions(true); }}
                            onFocus={() => { if (categoryName.trim()) setShowSuggestions(true); }}
                            required
                            autoComplete="off"
                          />
                          {showSuggestions && suggestions.length > 0 && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                              background: '#fff', border: '1px solid #dee2e6', borderRadius: '0 0 4px 4px',
                              maxHeight: '150px', overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                              {suggestions.map(tag => (
                                <div
                                  key={tag.id}
                                  style={{ padding: '8px 12px', cursor: 'pointer' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                                  onClick={() => { setCategoryName(tag.category); setShowSuggestions(false); }}
                                >
                                  {tag.category}
                                </div>
                              ))}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>Subcategory</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter subcategory name"
                            value={subcategoryName}
                            onChange={(e) => setSubcategoryName(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Button
                      type="submit"
                      className="sxi_primary_blue_btn btn-height"
                      disabled={saving || !categoryName.trim() || !subcategoryName.trim()}
                    >
                      {saving ? <><Spinner size="sm" animation="border" className="me-2" />Adding...</> : 'Add Tag'}
                    </Button>
                  </Form>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="view-tags">
                <Accordion.Header>
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="sxi-accordion-header-with-icon">
                      <span>Saved Section Tags</span>
                    </div>
                    <span className={`campaign-accordion-chevron ${activeKeys.includes('view-tags') ? 'open' : ''}`} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" />
                    </div>
                  ) : tags.length === 0 ? (
                    <p className="mb-0 mt-1">No tags found. Add your first tag above.</p>
                  ) : (
                    <div className="opportunities-keywords-table border rounded bg-white opp-padding">
                      <Table responsive className="mb-0">
                        <thead>
                          <tr>
                            <th className="channel_performance_header_cell">#</th>
                            <th className="channel_performance_header_cell">Category</th>
                            <th className="channel_performance_header_cell">Subcategories</th>
                            <th className="channel_performance_header_cell">Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tags.map((tag, index) => (
                            <tr key={tag.id} className={index % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}>
                              <td className="channel_performance_table_cell">{index + 1}</td>
                              <td className="channel_performance_table_cell">{tag.category}</td>
                              <td className="channel_performance_table_cell">
                                {tag.subcategories.slice(0, 3).map((sc, i) => (
                                  <Badge key={i} bg="secondary" className="me-1 mb-1">{sc}</Badge>
                                ))}
                                {tag.subcategories.length > 3 && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ms-1"
                                    onClick={() => setShowAllTag(tag)}
                                  >
                                    +{tag.subcategories.length - 3} more
                                  </Button>
                                )}
                              </td>
                              <td className="channel_performance_table_cell">{new Date(tag.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </Col>
      </Row>
      <Modal show={!!showAllTag} onHide={() => setShowAllTag(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Subcategories - {showAllTag?.category}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showAllTag?.subcategories.map((sc, i) => (
            <Badge key={i} bg="secondary" className="me-1 mb-2" style={{ fontSize: '0.9em' }}>{sc}</Badge>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button className='sxi_primary_blue_btn btn-height' onClick={() => setShowAllTag(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SectionTags;
